import { Alert, Button, Card, Dialog, PasswordField, TextArea, useToast } from '@aulaviva/ui';
import { Download, UserX } from 'lucide-react';
import { useState } from 'react';
import { urlEsportazioneDati, useChiediCancellazione, useMiaCancellazione } from '@/api/amministrazione';
import { ErroreRichiesta } from '@/api/client';
import { formattaDataOra } from '@/lib/date';
import { amministrazione, comuni } from '@/testi';

/** Sezione GDPR del profilo: scarica i tuoi dati, chiedi la cancellazione. */
export function ITuoiDati({ primario = true }: { primario?: boolean }) {
  const t = amministrazione.tuoiDati;
  const toast = useToast();
  const mia = useMiaCancellazione();
  const chiedi = useChiediCancellazione();
  const [aperto, setAperto] = useState(false);
  const [password, setPassword] = useState('');
  const [motivo, setMotivo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const inAttesa = mia.data?.stato === 'in_attesa';

  async function invia() {
    setErrore(null);
    if (!password) {
      setErrore(t.passwordErrata);
      return;
    }
    try {
      await chiedi.mutateAsync({ password, motivo: motivo.trim() || undefined });
      setAperto(false);
      setPassword('');
      setMotivo('');
      toast.mostra({ variant: 'success', title: t.inviata });
    } catch (e) {
      if (e instanceof ErroreRichiesta && (e.status === 401 || e.status === 400)) setErrore(t.passwordErrata);
      else setErrore(e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  return (
    <Card className="flex flex-col gap-4" data-testid="i-tuoi-dati">
      <h2 className="t-title-2">{t.titolo}</h2>
      <p className="t-body text-ink-muted">{t.intro}</p>
      {mia.data?.stato && (
        <Alert variant={mia.data.stato === 'rifiutata' ? 'warning' : 'info'} title={t.statoTitolo}>
          {t.stati[mia.data.stato]}
          {mia.data.richiestaAt && <> {t.richiestaIl(formattaDataOra(mia.data.richiestaAt))}</>}
        </Alert>
      )}
      <div className="flex flex-wrap gap-3">
        <Button variant={primario ? 'primary' : 'secondary'} icon={Download} onClick={() => window.open(urlEsportazioneDati(), '_blank', 'noopener')} title={t.scaricaAiuto}>
          {t.scarica}
        </Button>
        {!inAttesa && mia.data?.stato !== 'approvata' && (
          <Button variant="discreet" icon={UserX} onClick={() => setAperto(true)}>
            {t.chiedi}
          </Button>
        )}
      </div>
      <p className="t-body-small text-ink-muted">{t.scaricaAiuto}</p>
      <Dialog
        open={aperto}
        onOpenChange={(o) => !o && setAperto(false)}
        title={t.chiediTitolo}
        description={t.chiediTesto}
        actions={
          <>
            <Button onClick={() => setAperto(false)}>{amministrazione.cancellazioni.annulla}</Button>
            <Button variant="primary" onClick={invia} loading={chiedi.isPending} loadingText={comuni.caricamento}>
              {t.conferma}
            </Button>
          </>
        }
      >
        {errore && <Alert variant="error" title={errore} />}
        <TextArea label={t.motivo} value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} maxLength={500} />
        <PasswordField label={t.password} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Dialog>
    </Card>
  );
}
